import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useObRiskWatchApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function ObRiskWatchPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OB') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const { fetching, data, pagination, columns: baseColumns, recompute } = useObRiskWatchApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    return [{
      type: 'edit',
      label: t('onboardingRisk.action.recompute'),
      onClick: async () => { await recompute(row.onboardingId); },
    }];
  }, [t, writable, recompute]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('onboardingRisk.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
